import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { luxuryHireApi, LuxuryVehicle } from '../../../services/luxuryHireApi';

const CATEGORY_LABELS: Record<string, string> = {
  luxury_sedan: 'Luxury Sedan',
  sports_car: 'Sports Car',
  suv: 'Luxury SUV',
  bus: 'Luxury Bus',
  van: 'Executive Van',
  ultra_luxury: 'Ultra Luxury',
};

const formatCurrency = (amount: number, currency: string = 'NGN') => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const VehicleDetail: React.FC = () => {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState<LuxuryVehicle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    if (vehicleId) {
      fetchVehicle(parseInt(vehicleId));
    }
  }, [vehicleId]);

  const fetchVehicle = async (id: number) => {
    try {
      setLoading(true);
      const data = await luxuryHireApi.getVehicle(id);
      setVehicle(data);
      setError(null);
    } catch (err: any) {
      setError('Failed to load vehicle details.');
      console.error('Error fetching vehicle:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBookNow = () => {
    navigate(`/luxury-hire/book/${vehicleId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-amber-500/30 rounded-full animate-spin border-t-amber-500"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-3xl">🚗</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center text-white">
        <div className="text-6xl mb-4">😔</div>
        <p className="text-slate-400 text-lg mb-4">{error || 'Vehicle not found'}</p>
        <button
          onClick={() => navigate('/luxury-hire')}
          className="px-6 py-2 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-colors"
        >
          Back to Catalog
        </button>
      </div>
    );
  }

  const allImages = vehicle.photos && vehicle.photos.length > 0 
    ? vehicle.photos 
    : vehicle.thumbnail_url 
      ? [vehicle.thumbnail_url] 
      : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/luxury-hire')}
          className="flex items-center text-slate-400 hover:text-white transition-colors mb-6"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Catalog
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-slate-800">
              {allImages.length > 0 ? (
                <img
                  src={allImages[selectedImageIndex]}
                  alt={vehicle.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-800">
                  <span className="text-8xl opacity-50">🚗</span>
                </div>
              )}
              
              {vehicle.is_featured && (
                <div className="absolute top-4 left-4 px-4 py-2 bg-amber-500 text-white font-medium rounded-full shadow-lg">
                  Featured
                </div>
              )}
            </div>

            {allImages.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {allImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImageIndex === index
                        ? 'border-amber-500'
                        : 'border-transparent hover:border-slate-600'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${vehicle.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div>
              <p className="text-amber-400 font-medium uppercase tracking-wider mb-1">
                {vehicle.brand}
              </p>
              <h1 className="text-4xl font-bold text-white">{vehicle.name}</h1>
              {vehicle.year && (
                <p className="text-slate-400 mt-1">{vehicle.year} Model</p>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-slate-700/50 text-slate-300 border border-slate-600">
                {CATEGORY_LABELS[vehicle.vehicle_category] || vehicle.vehicle_category}
              </span>
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-slate-700/50 text-slate-300 border border-slate-600">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
                {vehicle.passenger_capacity} Passengers
              </span>
              {vehicle.color && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-slate-700/50 text-slate-300 border border-slate-600">
                  {vehicle.color}
                </span>
              )}
            </div>

            {vehicle.description && (
              <p className="text-slate-300 leading-relaxed">{vehicle.description}</p>
            )}

            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Pricing</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-slate-700/30 rounded-xl">
                  <p className="text-slate-400 text-sm mb-1">Hourly</p>
                  <p className="text-amber-400 font-bold text-xl">
                    {formatCurrency(vehicle.hourly_rate, vehicle.currency)}
                  </p>
                </div>
                {vehicle.half_day_rate && (
                  <div className="text-center p-4 bg-slate-700/30 rounded-xl">
                    <p className="text-slate-400 text-sm mb-1">Half Day</p>
                    <p className="text-amber-400 font-bold text-xl">
                      {formatCurrency(vehicle.half_day_rate, vehicle.currency)}
                    </p>
                    <p className="text-slate-500 text-xs">{vehicle.included_hours_half_day} hrs</p>
                  </div>
                )}
                <div className="text-center p-4 bg-slate-700/30 rounded-xl">
                  <p className="text-slate-400 text-sm mb-1">Full Day</p>
                  <p className="text-amber-400 font-bold text-xl">
                    {formatCurrency(vehicle.daily_rate, vehicle.currency)}
                  </p>
                  <p className="text-slate-500 text-xs">{vehicle.included_hours_daily} hrs</p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">Included KM:</span>
                  <span className="text-white ml-2">{vehicle.included_km} km</span>
                </div>
                <div>
                  <span className="text-slate-400">Extra KM:</span>
                  <span className="text-white ml-2">{formatCurrency(vehicle.extra_km_rate, vehicle.currency)}/km</span>
                </div>
                {vehicle.extra_hour_rate && (
                  <div>
                    <span className="text-slate-400">Extra Hour:</span>
                    <span className="text-white ml-2">{formatCurrency(vehicle.extra_hour_rate, vehicle.currency)}/hr</span>
                  </div>
                )}
                <div>
                  <span className="text-slate-400">Deposit:</span>
                  <span className="text-white ml-2">{vehicle.deposit_percentage}%</span>
                </div>
              </div>
            </div>

            {vehicle.security_required && (
              <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                <span className="text-2xl">🛡️</span>
                <div>
                  <p className="text-amber-400 font-medium">Security Included</p>
                  <p className="text-slate-400 text-sm">
                    This vehicle comes with mandatory security personnel ({formatCurrency(vehicle.security_fee, vehicle.currency)}/day)
                  </p>
                </div>
              </div>
            )}

            {vehicle.features && vehicle.features.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Features & Amenities</h3>
                <div className="grid grid-cols-2 gap-2">
                  {vehicle.features.map((feature, index) => (
                    <div key={index} className="flex items-center text-slate-300">
                      <svg className="w-5 h-5 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-4 pt-4">
              <button
                onClick={handleBookNow}
                className="flex-1 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold text-lg hover:from-amber-600 hover:to-orange-600 transition-colors shadow-lg shadow-amber-500/25"
              >
                Book This Vehicle
              </button>
            </div>

            <div className="flex items-center justify-center gap-6 text-slate-400 text-sm pt-2">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Min {vehicle.minimum_hours} hours
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Book up to {vehicle.advance_booking_days} days ahead
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetail;
